import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Loader from "../components/common/Loader";
import ErrorState from "../components/common/ErrorState";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import roundsService from "../services/roundsService";
import universityService from "../services/universityService";
import panelUsersService from "../services/panelUsersService";

const ROUND_TYPES = [
  { value: "group_discussion", label: "Group Discussion" },
  { value: "personal_interview", label: "Personal Interview" },
  { value: "case_study", label: "Case Study" },
  { value: "aptitude_test", label: "Aptitude Test" },
  { value: "technical_interview", label: "Technical Interview" },
  { value: "hr_round", label: "HR Round" },
  { value: "other", label: "Other" },
];

export default function RoundsManagement() {
  const { id } = useParams();
  const [university, setUniversity] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [panelUsers, setPanelUsers] = useState([]);
  const [state, setState] = useState("loading");
  const [newRoundName, setNewRoundName] = useState("");
  const [newRoundType, setNewRoundType] = useState("other");
  const [adding, setAdding] = useState(false);
  const [pickerRoundId, setPickerRoundId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [syncMessage, setSyncMessage] = useState("");
    const [releasingId, setReleasingId] = useState(null);
  const [releaseMessage, setReleaseMessage] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [editForm, setEditForm] = useState({ round_name: "", round_type: "other", description: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  function load() {
    setState("loading");
    Promise.all([universityService.get(id), roundsService.listByUniversity(id), panelUsersService.list()])
      .then(([uRes, rRes, pRes]) => {
        setUniversity(uRes.data);
        setRounds(rRes.data || []);
        setPanelUsers(pRes.data || []);
        setState("ready");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, [id]); // eslint-disable-line

  async function addRound(e) {
    e.preventDefault();
    if (!newRoundName.trim()) return;
    setAdding(true);
    try {
      await roundsService.create(id, { round_name: newRoundName.trim(), round_type: newRoundType });
      setNewRoundName("");
      setNewRoundType("other");
      load();
    } catch (_) {
    } finally {
      setAdding(false);
    }
  }

  async function toggleRound(round) {
    await roundsService.setStatus(round.id, round.is_active ? 0 : 1);
    load();
  }

  async function tagPanel(roundId, panelUserId) {
    if (!panelUserId) return;
    await roundsService.assignPanel(roundId, panelUserId);
    setPickerRoundId(null);
    load();
  }

  async function untagPanel(roundId, panelUserId) {
    await roundsService.unassignPanel(roundId, panelUserId);
    load();
  }

  async function syncApplicants(roundId) {
    setSyncingId(roundId);
    setSyncMessage("");
    try {
      const res = await roundsService.syncApplicants(roundId);
      setSyncMessage(res.message || "Sync complete.");
      load();
    } catch (err) {
      setSyncMessage(err.friendlyMessage || "Could not sync applicants.");
    } finally {
      setSyncingId(null);
    }
  }

    async function releaseShortlisted(round) {
    if (releasingId !== null) return;

    const confirmed = window.confirm(
      `Release all currently unreleased shortlisted results for "${round.round_name}"?\n\n` +
      "Students will see their results and emails will be attempted."
    );

    if (!confirmed) return;

    setReleasingId(round.id);
    setReleaseMessage("");

    try {
      const result = await roundsService.releaseShortlisted(round.id);

      setReleaseMessage(
        result.message || "Shortlisted results released."
      );

      load();
    } catch (err) {
      setReleaseMessage(
        err.response?.data?.message ||
        err.friendlyMessage ||
        "Could not confirm release. Refresh the page before trying again."
      );
    } finally {
      setReleasingId(null);
    }
  }

  function openEditRound(round) {
    setEditingRound(round);
    setEditForm({
      round_name: round.round_name || "",
      round_type: round.round_type || "other",
      description: round.description || "",
    });
    setEditError("");
    setEditModalOpen(true);
  }

  async function handleEditSave(e) {
    e.preventDefault();
    if (!editForm.round_name.trim()) {
      setEditError("Round name is required.");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await roundsService.update(editingRound.id, {
        round_name: editForm.round_name.trim(),
        round_type: editForm.round_type,
        description: editForm.description || null,
      });
      setEditModalOpen(false);
      load();
    } catch (err) {
      setEditError(err.friendlyMessage || "Could not save changes.");
    } finally {
      setEditSaving(false);
    }
  }

  if (state === "loading") return <div className="pm-page"><Loader label="Loading rounds" /></div>;
  if (state === "error") return <div className="pm-page"><ErrorState onRetry={load} /></div>;

  return (
    <div className="pm-page">
      <Link to={`/admin/universities/${id}`} className="pm-back-link">← {university.name}</Link>
      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">Hiring rounds</span>
          <h1>{university.name}</h1>
        </div>
      </header>

      {syncMessage && <div className="pm-sync-message">{syncMessage}</div>}
            {releaseMessage && (
        <div className="pm-sync-message" role="status">
          {releaseMessage}
        </div>
      )}

      <form className="pm-card pm-add-round-form" onSubmit={addRound}>
        <select value={newRoundType} onChange={(e) => setNewRoundType(e.target.value)}>
          {ROUND_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          placeholder="Round name — e.g. Group Discussion, PI 1"
          value={newRoundName}
          onChange={(e) => setNewRoundName(e.target.value)}
        />
        <button className="pm-btn pm-btn--primary" disabled={adding} type="submit">
          {adding ? "Adding…" : "+ Add round"}
        </button>
      </form>

      {!rounds.length ? (
        <EmptyState title="No rounds defined yet" description="Add your first round above — students will be placed into it automatically as they apply." />
      ) : (
        <div className="pm-round-manage-list">
          {rounds.map((r, i) => (
            <motion.div
              key={r.id}
              className="pm-card pm-round-manage-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.04 }}
            >
              <div className="pm-round-manage-card__head">
                <div>
                  <span className="pm-round-manage-card__order">Round {r.round_order}</span>
                  <h3>{r.round_name}</h3>
                  {r.round_type && r.round_type !== "other" && (
                    <span className="pm-round-type-badge">{ROUND_TYPES.find((t) => t.value === r.round_type)?.label || r.round_type}</span>
                  )}
                </div>
                <div className="pm-round-manage-card__meta">
                  <span>{r.applicant_count} applicant{r.applicant_count === 1 ? "" : "s"}</span>
                  <button
                    className="pm-btn pm-btn--outline pm-btn--sm"
                    disabled={syncingId === r.id}
                    onClick={() => syncApplicants(r.id)}
                    title="Pull in any student who applied to this campus but isn't showing up in a round yet"
                  >
                    {syncingId === r.id ? "Syncing…" : "Sync applicants"}
                  </button>
                                    <button
                    type="button"
                    className="pm-btn pm-btn--primary pm-btn--sm"
                    disabled={releasingId !== null || !r.is_active}
                    onClick={() => releaseShortlisted(r)}
                  >
                    {releasingId === r.id
                      ? "Releasing…"
                      : "Release shortlisted"}
                  </button>
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => openEditRound(r)}>
                    Edit
                  </button>
                  <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => toggleRound(r)}>
                    {r.is_active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>

              <div className="pm-round-manage-card__panel">
                <span className="pm-round-manage-card__panel-label">Panel:</span>
                {r.panel.length === 0 && <span className="pm-table__muted">No panel tagged yet</span>}
                {r.panel.map((p) => (
                  <span className="pm-panel-chip" key={p.id}>
                    {p.name}
                    <button onClick={() => untagPanel(r.id, p.id)}>✕</button>
                  </span>
                ))}

                {pickerRoundId === r.id ? (
                  <select autoFocus onChange={(e) => tagPanel(r.id, e.target.value)} onBlur={() => setPickerRoundId(null)}>
                    <option value="">Select panel member…</option>
                    {panelUsers.filter((pu) => !r.panel.some((p) => p.id === pu.id)).map((pu) => (
                      <option key={pu.id} value={pu.id}>{pu.name} ({pu.email})</option>
                    ))}
                  </select>
                ) : (
                  <button className="pm-btn pm-btn--outline pm-btn--sm" onClick={() => setPickerRoundId(r.id)}>+ Tag panel</button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit round">
        <form onSubmit={handleEditSave} className="pm-modal-form">
          <div className="pm-field">
            <label>Round name *</label>
            <input
              value={editForm.round_name}
              onChange={(e) => setEditForm({ ...editForm, round_name: e.target.value })}
              required
            />
          </div>
          <div className="pm-field">
            <label>Round type</label>
            <select
              value={editForm.round_type}
              onChange={(e) => setEditForm({ ...editForm, round_type: e.target.value })}
            >
              {ROUND_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="pm-field">
            <label>Description (optional)</label>
            <textarea
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Any notes for panel members about this round"
            />
          </div>
          {editError && <div className="pm-form-error">{editError}</div>}
          <button className="pm-btn pm-btn--primary" disabled={editSaving} type="submit">
            {editSaving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </Modal>
    </div>
  );
}